import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { collection, getDocs } from 'firebase/firestore';

interface EventResult {
    id: string;
    eventName: string;
    eventDate: string;
    location: string;
    game: string;
    placement: string;
    players?: string;
}

// Fallback data if Firestore is empty
const defaultResults: Omit<EventResult, 'id'>[] = [
    {
        eventName: 'MIHSEF State Finals',
        eventDate: 'December 2025',
        location: 'UM-Flint',
        game: 'SSBU Crews',
        placement: '2nd Place',
        players: 'PandaMan22 (C), Taco, Beasty5932',
    },
    {
        eventName: 'MIHSEF State Finals',
        eventDate: 'December 2025',
        location: 'UM-Flint',
        game: 'SSBU Solos',
        placement: '3rd Place',
        players: 'PandaMan22',
    },
    {
        eventName: 'MIHSEF State Finals',
        eventDate: 'December 2025',
        location: 'UM-Flint',
        game: 'Mario Kart 8',
        placement: '14th Place',
    },
];

export default function TrophyGalleryPage() {
    const [results, setResults] = useState<EventResult[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function loadResults() {
            try {
                const snap = await getDocs(collection(db, 'eventResults'));
                const data = snap.docs.map(d => ({ id: d.id, ...d.data() } as EventResult));
                if (data.length > 0) {
                    setResults(data);
                } else {
                    // Use defaults
                    setResults(defaultResults.map((r, i) => ({ ...r, id: `default-${i}` })));
                }
            } catch (err) {
                console.error('Error loading results:', err);
                setResults(defaultResults.map((r, i) => ({ ...r, id: `default-${i}` })));
            } finally {
                setLoading(false);
            }
        }
        loadResults();
    }, []);

    const getPlacementStyle = (placement: string) => {
        if (placement.includes('1st')) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
        if (placement.includes('2nd')) return 'bg-gray-400/20 text-gray-300 border-gray-400/50';
        if (placement.includes('3rd')) return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
        return 'bg-secondary-700 text-secondary-300 border-secondary-600';
    };

    const getPlacementEmoji = (placement: string) => {
        if (placement.includes('1st')) return '🥇';
        if (placement.includes('2nd')) return '🥈';
        if (placement.includes('3rd')) return '🥉';
        return '🏆';
    };

    // Group by event
    const eventGroups = results.reduce((acc, r) => {
        const key = `${r.eventName}-${r.eventDate}`;
        if (!acc[key]) {
            acc[key] = { eventName: r.eventName, eventDate: r.eventDate, location: r.location, results: [] };
        }
        acc[key].results.push(r);
        return acc;
    }, {} as Record<string, { eventName: string; eventDate: string; location: string; results: EventResult[] }>);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="text-5xl mb-4">🏆</div>
                <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-2">
                    Trophy <span className="text-primary-500">Gallery</span>
                </h1>
                <p className="text-secondary-400">Peck Pirates Championship History</p>
            </div>

            {/* Trophy Cards */}
            <div className="space-y-8 mb-12">
                {Object.values(eventGroups).map((group, idx) => (
                    <section key={idx} className="card animate-slide-up" style={{ animationDelay: `${idx * 0.1}s` }}>
                        <div className="border-b border-secondary-700 pb-4 mb-4">
                            <h2 className="text-xl font-display font-bold text-white">{group.eventName}</h2>
                            <p className="text-secondary-400 text-sm">{group.eventDate} • {group.location}</p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {group.results.map((result, i) => (
                                <div
                                    key={i}
                                    className={`p-4 rounded-lg border-2 ${getPlacementStyle(result.placement)}`}
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="text-2xl">{getPlacementEmoji(result.placement)}</span>
                                        <div>
                                            <h3 className="font-semibold text-white">{result.game}</h3>
                                            <p className="text-lg font-bold">{result.placement}</p>
                                        </div>
                                    </div>
                                    {result.players && (
                                        <p className="text-sm text-secondary-400 mt-2">{result.players}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                ))}
            </div>

            {/* Coming Soon */}
            <section className="card border-dashed border-secondary-600 text-center py-8 mb-8">
                <h3 className="text-lg font-semibold text-secondary-400 mb-2">More Events Coming Soon</h3>
                <p className="text-secondary-500 text-sm">Confluence Festival • River City Invitational</p>
            </section>

            {/* Contact */}
            <div className="text-center">
                <Link to="/records" className="btn-primary">View Player Records →</Link>
            </div>
        </div>
    );
}
